import { useNavigate } from 'react-router-dom'
import { type ReactElement, type FC, useState, type ChangeEvent } from 'react'
import { useCheckoutPlan, MTDtracking, useResponsive, loadHomepageCharityCampaigns, ActiveCampaign, type CharityCampaign } from '@shamaazi/mytennights'
import { Country, getUserCountry, isMultiCharity } from '@lib/services'
import { useAuth } from '@lib/hooks'
import { Button, CountrySelection, Input, SearchIcon, Spinner } from '@lib/components'
import { useQuery } from 'react-query'
import { CharityCard } from '~/components/CharityCard'
import { CauseCard } from '~/components/CauseCard'
import { MytennightsTestId } from '@lib/testing'
import whiteArrow from '~/assets/icons/arrow-right-white.svg'
const TabType = {
  Cause: 'cause',
  Charity: 'charity'
} as const

type TabTypeValue = typeof TabType[keyof typeof TabType]

const charitiesFromUK: string[] = [
  'orphans-in-need-uk',
  'muslim-aid-uk',
  'islamic-relief-uk'
]

const exclusionsByCountry: { [key in Country]?: string[] } = {
  [Country.CA]: ['islamic-relief-uk']
}

const pennyAppealUkCharityId: string = 'penny-appeal-uk'

export const CharityCauses: FC<{ nextPage: string }> = ({ nextPage }) => {
  const { plan, setPlan } = useCheckoutPlan()
  const { user } = useAuth()
  const { isMobile } = useResponsive()
  const [activeTab, setActiveTab] = useState<TabTypeValue>(TabType.Cause)
  const navigate = useNavigate()
  const { isLoading, error, data: charities } = useQuery('charities', async () => loadHomepageCharityCampaigns(ActiveCampaign.myTenDays))
  const countries = [...new Set(charities?.map(c => c.country))]
  const [country, setCountry] = useState<Country>(getUserCountry())
  const [searchState, setSearchState] = useState('')

  const onSearchTextChanged = (e: ChangeEvent<HTMLInputElement>): void => {
    setSearchState(e.target.value)
  }

  const renderTab = (tabName: TabTypeValue, index: number): ReactElement => {
    return <>
      {<button
        key={tabName}
        className={`flex-1 h-11 py-2.5 px-5 rounded-3xl flex items-center justify-center ${index > 0 ? '-ml-7' : ''} ${activeTab === tabName
          ? 'bg-mtd-secondary text-white font-bold z-20'
          : 'bg-mtd-secondary-1 text-mtd-secondary-4 font-bold'
          }`}
        data-test-id={tabName === TabType.Cause ? MytennightsTestId.causeTab : MytennightsTestId.charityTab}
        onClick={() => setActiveTab(tabName)}
      >
        {tabName.charAt(0).toUpperCase() + tabName.slice(1)}{tabName === TabType.Cause && <span className='font-semibold mx-2 bg-mtn-yellow p-1 text-xs rounded text-mtn-blue-250'>NEW</span>}
      </button>}
    </>
  }

  const flag = <img alt="Flag" width={24} height={24} className="absolute pointer-events-none top-2/4 left-3 transform -translate-y-1/2 h-7 w-7" src={`/assets/flags/${country}.svg`} />

  const filterUKCharityIntoOtherCountries = (): CharityCampaign[] => {
    if (country === Country.US || country === Country.AU || country === Country.CA) {
      // Filter based on UK charities and apply any exclusions for the current country
      const exclusions = exclusionsByCountry[country] ?? []
      return charities?.filter(c => charitiesFromUK.includes(c.charity_id) && !exclusions.includes(c.charity_id)) ?? []
    }
    return []
  }

  const filterOutPennyAppealUk = (charity: CharityCampaign): boolean => {
    return !charity.charity_id.toLowerCase().includes(pennyAppealUkCharityId)
  }

  const pennyAppealUkCharityCampaign = (withPennyAppeal: boolean): CharityCampaign[] => {
    return withPennyAppeal && country === Country.GB ? charities?.filter(c => pennyAppealUkCharityId === c.charity_id) ?? [] : []
  }

  const filterCharitiesBySearchTerm = (charity: CharityCampaign): boolean => {
    const searchTerm = searchState.trim().toLowerCase()
    return charity.charity_name.toLowerCase().includes(searchTerm) || charity.charity_id.toLowerCase().includes(searchTerm) ||
      charity.causes?.find(c => c.title.toLowerCase().includes(searchTerm)) !== undefined
  }

  const onSelect = (charity: CharityCampaign): void => {
    const updatedPlan = {
      ...plan,
      charity
    }
    setPlan(updatedPlan)
    navigate(nextPage)
    MTDtracking.selectCharity(user?.email, updatedPlan)
  }

  const renderCharityCards = (withPennyAppealUk: boolean): ReactElement => {
    const filteredCharities = charities?.filter((c) => c.country === country).concat(...filterUKCharityIntoOtherCountries()).filter(filterOutPennyAppealUk).concat(pennyAppealUkCharityCampaign(withPennyAppealUk)).filter(filterCharitiesBySearchTerm)
    return <>
      {filteredCharities?.map((c) => {
        return <CharityCard key={c.charity_id} onSelect={() => onSelect(c)} charity={c} />
      })}
    </>
  }
  const renderCauseCards = (): ReactElement => {
    const filteredCharities = charities?.filter((c) => c.country === country && isMultiCharity(c.charity_id))
    return <>
      {filteredCharities?.map((c) => (
        <CauseCard key={c.charity_id} charity={c} causes={c.causes} />
      ))}
    </>
  }

  const renderContinueBtn = (): ReactElement => {
    return <div className='flex flex-col w-full justify-center items-center'>
      <Button
        data-test-id={MytennightsTestId.continueButton}
        variant="mtd-primary"
        className='text-2xl w-full md:w-80 uppercase font-bold'
        disabled={plan.selectedCauses.length === 0}
        onClick={() => {
          setPlan({ ...plan, causes: {} })
          MTDtracking.confirmMCPCausesSelected(user?.email, plan)
          navigate(nextPage)
        }}
      >
        <div className='flex gap-x-4 justify-center text-fg-H2 text-white'>
          Continue <img src={whiteArrow} alt="right arrow" />
        </div>
      </Button>
    </div>
  }

  return (
    <div>
      {isLoading ? <Spinner className="text-mtn-blue mt-2 mx-auto" /> : error ? <p className="font-medium text-center text-mtn-red">Sorry, we were unable to load any charities!</p> : <>

        {/* Cause/charity + country select + search */}
        <div className="w-full max-w-fg-screen-small mx-auto">
          <div className="flex flex-col items-center">
            <div className='text-center w-full flex flex-col items-center max-w-fg-screen-small'>
              {!isMobile && <h1 className="w-full text-mtd-h3 font-bold text-mtd-secondary-5">Choose who you want to help</h1>}
              <div className="my-5 w-full">
                <div className="flex bg-fg-gray-700 rounded-3xl">
                  {[TabType.Cause, TabType.Charity].map((tab, index) => renderTab(tab as TabTypeValue, index))}
                </div>
              </div>
              <div className="w-full">
                <CountrySelection
                    countries={countries}
                    value={country}
                    className={`w-full max-w-full rounded-lg pl-12 ${activeTab === TabType.Cause ? '' : ''}`}
                    onCountryChange={(v: Country) => {
                      setCountry(v)
                      setPlan({ ...plan, selectedCauses: [] })
                      MTDtracking.changeCountry(user?.email, plan)
                    }}
                    FGFlag={flag}
                    variant="mtn"
                />
                {activeTab !== TabType.Cause && <div className="my-4 relative w-full">
                  <SearchIcon />
                  <Input variant="mtn" value={searchState} onChange={onSearchTextChanged}
                    placeholder="Find a charity or cause" className="w-full max-w-full rounded-lg pl-12" />
                </div>}
              </div>
              <p className="text-mtd-info mt-4 text-mtd-gray-6 font-normal">All donations are tax deductible {country === Country.US ? 'when giving to charities located in the region you are based only.' : ''}</p>
              <p className="text-mtd-info text-mtd-gray-6 font-normal">{activeTab === TabType.Cause ? 'Select up to 4 causes.' : ''}</p>
            </div>
          </div>
        </div>

        {activeTab === TabType.Charity && <div className="flex justify-between my-5">
          <div className="w-full grid grid-cols-1 gap-4 place-content-center lg:grid-cols-3 md:grid-cols-2 sm:gap-4">
            {renderCharityCards(true)}
          </div>
        </div>}
        {activeTab === TabType.Cause && <div className="flex flex-col justify-between my-5">
          <div className="gap-4 w-full mb-7">
            {renderCauseCards()}
          </div>
          {renderContinueBtn()}
        </div>}
      </>
      }
    </div>)
}
